import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Goals from './Goals';
import QuickJot from './QuickJot';
import Home from './Home';
import LimitBanner from './LimitBanner';
import ManualTrade from './ManualTrade';
import InsightsPage from './InsightsPage';
import AccountComparePage from './AccountComparePage';
import { DemoBanner, DemoGate } from './Demo';
import Plans, { ProGate, ProLock, TrialBanner, isLocked } from './Plans';
import Playbook from './Playbook';
import Routine from './Routine';
import Session from './Session';
import Settings from './Settings';
import ShareCard from './ShareCard';
import {
  AccountManager, ImportAccounts, accountLabel, initialAssignment, needsAccountChoice, resolveImportAccounts,
} from './Accounts';
import { api } from './api';
import { AppSidebar, AppTopBar, JOURNEY_PART_VIEWS, canSeeView, featureOpen } from './AppNav';
import { ACADEMY_VIEWS, AcademyTopBar } from './Academy';
import DashboardPage from './DashboardPage';
import TradeEditor from './TradeEditor';
import { TagChip, tagKind } from './tags';
import { GradeBadge, gradeScore } from './grades';
import { DISCORD_URL } from './config';
import { parseTradesCsv, roundTripCommission } from './csv';
import { canReadPictures, isPicture, readPictures } from './aiImport';
import { PENDING_IMPORT_KEY } from './FreeReportCard';
import { dayKey, formatDuration, formatMoney, grossPnl, holdMs, netPnl, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { trackFirstTrade } from './analytics';
import TradeDays, { AllTradesSummary } from './TradeDays';
import { MarketAlertRunner, MarketCalendarPage, ToastHost } from './MarketCalendar';
import AccountsPage from './AccountsPage';
import AddAccountWizard from './AddAccountWizard';
import PlatformRequest from './PlatformRequest';
import { PinnedReminders, ReminderAlertRunner } from './Reminders';
import { detectBehavior } from './behaviorDetect';
import JourneyMoney from './JourneyMoney';
import JourneyBetter from './JourneyBetter';
import JourneyGrowth from './JourneyGrowth';
import JourneyIdentity from './JourneyIdentity';
import { JourneyHub } from './JourneyPages';
import { JOURNEY_SCOPES, hasBothKinds, scopeJourney } from './journey';
import MarkPage from './MarkPage';
import TradeToolbar, { EMPTY_FILTERS, FilterNote, applyFilters, countFilters, describeFilters, rangeSaved, rangeText, readRange, saveRange } from './TradeToolbar';
import { setDisplayActive, showResult, tradeMoves, useDisplayMode } from './display';
import { setUsualRisk } from './rMultiple';

const JOURNEY_VIEWS = ['mark', ...JOURNEY_PART_VIEWS];
// The Journey's old tabs (bookmarks, old links) send people to the part that took their place.
const OLD_JOURNEY_TO_PART = {
  journey: '/app/journey/profitable', money: '/app/journey/profitable', progress: '/app/journey/better', recap: '/app/journey/better',
  timeline: '/app/journey/edge-milestones', edge: '/app/journey/edge-milestones', milestones: '/app/journey/edge-milestones',
};

// The notebook and its editor load only when the Notebook page opens.
const Notebook = lazy(() => import('./Notebook'));
const PracticePage = lazy(() => import('./PracticePage'));
const BacktestPage = lazy(() => import('./BacktestPage'));
const CoursePage = lazy(() => import('./course/CoursePage'));
const AcademyHome = lazy(() => import('./AcademyHome'));

const COMMISSION_KEY = 'mm-commission-per-side';


const DISCORD_CARD_KEY = 'mm-discord-card-dismissed';
const TRADE_SORT_KEY = 'mm-trade-sort';

// Sortable columns of the trades table. Numbers start biggest-first, text A to Z.
const TRADE_COLUMNS = [
  { key: 'date', label: 'Date', numeric: true },
  { key: 'account', label: 'Account', allOnly: true },
  { key: 'symbol', label: 'Symbol' },
  { key: 'side', label: 'Side' },
  { key: 'size', label: 'Size', numeric: true },
  { key: 'entry', label: 'Entry', numeric: true },
  { key: 'exit', label: 'Exit', numeric: true },
  { key: 'duration', label: 'Duration', numeric: true },
  { key: 'pnl', label: 'P&L', numeric: true },
  { key: 'fees', label: 'Fees', numeric: true },
  { key: 'net', label: 'Net', numeric: true },
  { key: 'grade', label: 'Grade', numeric: true },
];

function readTradeSort() {
  try {
    const saved = JSON.parse(localStorage.getItem(TRADE_SORT_KEY));
    if (saved && TRADE_COLUMNS.some((c) => c.key === saved.key) && (saved.dir === 'asc' || saved.dir === 'desc')) return saved;
  } catch {
    // Fall back to newest first.
  }
  return { key: 'date', dir: 'desc' };
}
const SELECTED_ACCOUNT_KEY = 'mm-selected-account';
const NAV_COLLAPSED_KEY = 'mm-nav-collapsed';
const TRADES_OPEN_KEY = 'mm-trades-open';
// Trades by day, by week or all in one table (remembered on this device).
const TRADES_VIEW_KEY = 'mm-trades-view';
const TRADES_VIEWS = [['day', 'Days'], ['week', 'Weeks'], ['all', 'All trades']];
const TRADES_VISIBLE = 15;

function readStored(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (e.g. private mode); the choice just isn't remembered.
  }
}

function readDismissed() {
  return readStored(DISCORD_CARD_KEY) === '1';
}


function Journal({ user, view = 'journal', onLogout, onUserChange, onSessionExpired }) {
  const { t, tr } = useI18n();
  // Dollars, %, points, ticks or Privacy (display.js). Privacy hides amounts only inside the journal.
  const displayMode = useDisplayMode();
  setDisplayActive(true);
  setUsualRisk(user && user.riskPerTrade);
  useEffect(() => { setDisplayActive(true); return () => setDisplayActive(false); }, []);
  const [trades, setTrades] = useState([]);
  const [error, setError] = useState('');
  // The day a trade is being added to by hand (YYYY-MM-DD), or null.
  const [addingTradeOn, setAddingTradeOn] = useState(null);
  // Which tab the Add a trade window opens on ('import' from the import links; else the last one used).
  const [addingTab, setAddingTab] = useState(null);
  // The trades list folds with its title (remembered), and shows about 15 trades at a time.
  const [tradesOpen, setTradesOpen] = useState(() => {
    try {
      return localStorage.getItem(TRADES_OPEN_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  // My Trading Journey's switch: prop firms, personal or all together (remembered).
  const [journeyScopePick, setJourneyScopePick] = useState(() => {
    try {
      return localStorage.getItem('mm-journey-scope');
    } catch {
      return null;
    }
  });
  const tradesBox = useRef(null);
  const [tradesBoxHeight, setTradesBoxHeight] = useState(null);
  const [tradesView, setTradesView] = useState(() => {
    try {
      const v = localStorage.getItem(TRADES_VIEW_KEY);
      return TRADES_VIEWS.some(([id]) => id === v) ? v : 'day';
    } catch {
      return 'day';
    }
  });
  const [tradeQuery, setTradeQuery] = useState('');
  // Filters and the date range from the bar at the top of the Dashboard and Trades pages.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [range, setRangeState] = useState(readRange);
  const setRange = (r) => { setRangeState(r); saveRange(r); };
  // Insights starts on the last 90 days the first time (when no date range was ever picked),
  // so habits fixed long ago don't keep showing up. It's a normal, visible choice to change.
  useEffect(() => {
    if (view === 'insights' && !rangeSaved() && range.preset === 'all') setRange({ preset: '90d', from: '', to: '' });
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  function chooseTradesView(v) {
    setTradesView(v);
    try {
      localStorage.setItem(TRADES_VIEW_KEY, v);
    } catch {
      // Not remembered in private mode.
    }
  }

  function toggleTrades() {
    setTradesOpen((o) => {
      try {
        localStorage.setItem(TRADES_OPEN_KEY, o ? 'closed' : 'open');
      } catch {
        // Not remembered in private mode.
      }
      return !o;
    });
  }
  const [csvResult, setCsvResult] = useState(null);
  // The file picked for import, kept so it can be sent to us when it doesn't import (PlatformRequest).
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  // How many screenshots or PDFs are being read for an import right now (0 when none).
  const [readingPictures, setReadingPictures] = useState(0);
  // The per-side commission rate is remembered on this browser for the next import.
  const [commission, setCommission] = useState(() => {
    try {
      return localStorage.getItem(COMMISSION_KEY) || '';
    } catch {
      return '';
    }
  });
  const [discordDismissed, setDiscordDismissed] = useState(readDismissed);
  const [library, setLibrary] = useState([]);
  const [editing, setEditing] = useState(null);
  const [accounts, setAccounts] = useState([]);
  // Which accounts are shown: 'all', one account id, or a list of two or more ids.
  const [selection, setSelection] = useState(() => {
    const saved = readStored(SELECTED_ACCOUNT_KEY) || 'all';
    if (saved.startsWith('[')) {
      try {
        const ids = JSON.parse(saved);
        if (Array.isArray(ids) && ids.length > 1 && ids.every((id) => typeof id === 'string' && id)) return ids;
      } catch {
        // Fall back to all accounts.
      }
      return 'all';
    }
    return saved;
  });
  // For the parts that show one account's rules: 'all', the account id, or 'multi'.
  const selected = Array.isArray(selection) ? 'multi' : selection;
  // The account ids being shown, or null for all of them.
  const shownIds = selection === 'all' ? null : Array.isArray(selection) ? selection : [selection];
  const [managingAccounts, setManagingAccounts] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [notes, setNotes] = useState({});
  const [focusMode, setFocusMode] = useState(false);
  // The sidebar folds into an icon rail on desktop (remembered on this browser) and
  // slides out as a drawer on phones.
  const [navCollapsed, setNavCollapsed] = useState(() => readStored(NAV_COLLAPSED_KEY) === 'collapsed');
  const [drawerOpen, setDrawerOpen] = useState(false);
  function toggleNav() {
    setNavCollapsed((c) => {
      writeStored(NAV_COLLAPSED_KEY, c ? 'expanded' : 'collapsed');
      return !c;
    });
  }
  useEffect(() => setDrawerOpen(false), [view]);
  const [tradeSort, setTradeSort] = useState(readTradeSort);
  const [plays, setPlays] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [sharing, setSharing] = useState(false);
  // What has finished loading, so the Getting started checklist doesn't flash for existing members.
  const [loaded, setLoaded] = useState({ trades: false, notes: false, plays: false });
  const markLoaded = (key) => setLoaded((l) => ({ ...l, [key]: true }));
  const navigate = useNavigate();
  const location = useLocation();

  // Arriving from Home's "Import trades": open Add a trade on Import a file once the journal has
  // loaded, or scroll to the import preview when a file is already being read.
  const scrollTo = location.state && location.state.scrollTo;
  useEffect(() => {
    if (view !== 'journal' || !scrollTo || !loaded.trades) return undefined;
    let pendingFile = false;
    try { pendingFile = Boolean(sessionStorage.getItem(PENDING_IMPORT_KEY)); } catch { /* none */ }
    if (scrollTo === 'import' && !csvResult && !pendingFile) {
      openImport();
      return undefined;
    }
    const id = setTimeout(() => document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(id);
  }, [view, scrollTo, loaded.trades, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  function openImport() {
    setAddingTab('import');
    setAddingTradeOn(dayKey(new Date()));
  }

  // A file from the free report card (FreeReportCard.js), kept until the visitor had an account:
  // open the Trades page with it ready in the import preview.
  useEffect(() => {
    if (user.isDemo || !loaded.trades) return;
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_IMPORT_KEY) || 'null');
    } catch {
      pending = null;
    }
    if (!pending || !pending.text) return;
    if (view !== 'journal') {
      navigate('/app', { state: { scrollTo: 'import' } });
      return;
    }
    try {
      sessionStorage.removeItem(PENDING_IMPORT_KEY);
    } catch {
      // Nothing to clean up.
    }
    const result = { fileName: pending.fileName || 'trades.csv', ...parseTradesCsv(pending.text) };
    setCsvResult(result);
    setAssignment(initialAssignment(result, accounts, selected));
    setTimeout(() => document.getElementById('import')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, [view, loaded.trades]); // eslint-disable-line react-hooks/exhaustive-deps

  // A link can choose which accounts to show (e.g. the demo banner's "Honest take" opens all accounts).
  const linkSelection = location.state && location.state.selectAccounts;
  useEffect(() => {
    if (linkSelection) selectAccount(linkSelection);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  function dismissDiscordCard() {
    setDiscordDismissed(true);
    writeStored(DISCORD_CARD_KEY, '1');
  }

  // A remembered account that no longer exists (deleted elsewhere, or an expired demo
  // copy) would show an empty journal, so fall back to all accounts.
  function keepSelectionValid(list) {
    setSelection((current) => {
      if (current === 'all') return current;
      const ids = (Array.isArray(current) ? current : [current]).filter((id) => list.some((a) => a._id === id));
      const next = ids.length === 0 ? 'all' : ids.length === 1 ? ids[0] : ids;
      writeStored(SELECTED_ACCOUNT_KEY, Array.isArray(next) ? JSON.stringify(next) : next);
      return next;
    });
  }

  // Swaps in updated copies of some trades (e.g. after fees were added to them).
  function replaceTrades(updated) {
    const byId = new Map(updated.map((x) => [x._id, x]));
    setTrades((list) => list.map((x) => byId.get(x._id) || x));
  }

  // Adds trades a sync brought in since the page loaded (the Live session checks for them),
  // and swaps in newer copies of ones already here.
  const mergeTrades = useCallback((fresh) => {
    if (!fresh.length) return;
    setTrades((list) => {
      const old = new Map(list.map((x) => [x._id, x]));
      const changed = fresh.filter((x) => !old.has(x._id) || old.get(x._id).updatedAt !== x.updatedAt);
      if (!changed.length) return list;
      const byId = new Map(changed.map((x) => [x._id, x]));
      return [...changed.filter((x) => !old.has(x._id)), ...list.map((x) => byId.get(x._id) || x)];
    });
  }, []);

  // `next` is 'all', one account id, or a list of ids (one id becomes that account,
  // none becomes all accounts).
  function selectAccount(next) {
    let value = next;
    if (Array.isArray(next)) value = next.length === 0 ? 'all' : next.length === 1 ? next[0] : next;
    setSelection(value);
    writeStored(SELECTED_ACCOUNT_KEY, Array.isArray(value) ? JSON.stringify(value) : value);
  }

  // Recounts trades per account after imports, moves and deletes.
  function refreshAccounts() {
    return api('/accounts')
      .then(setAccounts)
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
  }

  // Live session notes that were added to trades' notes (sessionLinks.js on the server).
  function applyLinkedNotes(list) {
    if (!list || !list.length) return;
    const byId = new Map(list.map((x) => [String(x._id), x.notes]));
    setTrades((all) => all.map((x) => (byId.has(String(x._id)) ? { ...x, notes: byId.get(String(x._id)) } : x)));
  }
  // A day note saved elsewhere on the page (Day insights marked as reviewed).
  useEffect(() => {
    const onNote = (e) => { const n = e.detail; if (n && n.date) setNotes((prev) => ({ ...prev, [n.date]: n })); };
    window.addEventListener('mm-note-saved', onNote);
    return () => window.removeEventListener('mm-note-saved', onNote);
  }, []);
  // Trades changed elsewhere on the page (linked trades, tags on several at once).
  useEffect(() => {
    const onReplaced = (e) => replaceTrades(e.detail || []);
    window.addEventListener('mm-trades-replaced', onReplaced);
    return () => window.removeEventListener('mm-trades-replaced', onReplaced);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onLinked = (e) => applyLinkedNotes(e.detail);
    window.addEventListener('mm-trade-notes', onLinked);
    return () => window.removeEventListener('mm-trade-notes', onLinked);
  }, []);

  // After merges or linking moves trades between accounts, reload both.
  function reloadAll() {
    return api('/accounts')
      .then((list) => {
        setAccounts(list);
        keepSelectionValid(list);
        return api('/trades');
      })
      .then(setTrades)
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
  }

  function handleAccountDeleted(id) {
    setAccounts((list) => list.filter((a) => a._id !== id));
    setTrades((list) => list.filter((t) => t.accountId !== id));
    if (shownIds && shownIds.includes(id)) selectAccount(shownIds.length > 1 ? shownIds.filter((x) => x !== id) : 'all');
  }
  const fileInput = useRef(null);

  // Sends the user back to the login page if their session ran out.
  function handleApiError(err, setMessage) {
    if (err.status === 401) return onSessionExpired();
    setMessage(err.message);
  }

  useEffect(() => {
    api('/tags')
      .then(setLibrary)
      .catch(() => {});
    api('/notes')
      .then((list) => setNotes(Object.fromEntries(list.map((n) => [n.date, n]))))
      .catch(() => {})
      .finally(() => markLoaded('notes'));
    api('/playbook')
      .then(setPlays)
      .catch(() => {})
      .finally(() => markLoaded('plays'));
    api('/experiments').then(setExperiments).catch(() => {});
  }, []);

  // Keeps the member's time zone up to date, so emails arrive at the right local time.
  useEffect(() => {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && user.timeZone !== zone && onUserChange) {
      api('/email/preferences', { method: 'PATCH', body: { timeZone: zone } }).then(onUserChange).catch(() => {});
    }
  }, [user.timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  // The "Getting started" checklist's buttons.
  function handleOnboarding(step) {
    // From Home, steps that happen in the journal open it first.
    if (view === 'home' && (step === 'trades' || step === 'review')) {
      navigate('/app', { state: { scrollTo: 'import' } });
      return;
    }
    if (step === 'account') navigate('/app/accounts');
    else if (step === 'trades') openImport();
    else if (step === 'morning') navigate('/app/morning');
    else if (step === 'reflection') navigate('/app/reflection');
    else if (step === 'playbook') navigate('/app/playbook');
    else if (step === 'review') {
      const latest = [...trades].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      if (latest) setEditing(latest);
      else document.getElementById('import')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function handleTradesCleared(accountId) {
    setTrades((list) => (accountId === 'all' ? [] : list.filter((t) => t.accountId !== accountId)));
    refreshAccounts();
  }

  // Size the trades box to the header plus the first 15 rows (never taller than most of the screen), so it scrolls past that.
  // Runs after every render (sorting, filters and edits change the rows) and only sets the height when it changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const box = tradesBox.current;
    const rows = box ? box.querySelectorAll('tbody tr') : [];
    let height = null;
    if (rows.length > TRADES_VISIBLE) {
      const row = rows[TRADES_VISIBLE - 1];
      // Measured inside the table, so scrolling the box doesn't change it.
      height = Math.ceil(row.offsetTop + row.offsetHeight + box.querySelector('table').offsetTop);
    }
    if (height !== tradesBoxHeight) setTradesBoxHeight(height);
  });

  function handleTradeSaved(updated) {
    setTrades((list) => list.map((t) => (t._id === updated._id ? updated : t)));
    setEditing(null);
    refreshAccounts();
  }

  function handleTradeDeleted(id) {
    setTrades((list) => list.filter((t) => t._id !== id));
    setEditing(null);
    refreshAccounts();
  }

  useEffect(() => {
    // Accounts load first: that also files any older trades into a default account.
    api('/accounts')
      .then((list) => {
        setAccounts(list);
        keepSelectionValid(list);
        return api('/trades');
      })
      .then((list) => {
        setTrades(list);
        markLoaded('trades');
      })
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
        else setError(err.message);
      });
  }, [onSessionExpired]);

  // Forget a remembered account that no longer exists or was archived.
  useEffect(() => {
    if (!shownIds || !accounts.length) return;
    const valid = shownIds.filter((id) => accounts.some((a) => a._id === id && !a.archived));
    if (valid.length !== shownIds.length) selectAccount(valid);
  }, [accounts, selection]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeAccounts = accounts.filter((a) => !a.archived);
  const accountById = Object.fromEntries(accounts.map((a) => [a._id, a]));
  // Day insights on the Trades page's days: open to members from Admin → Features; admins always.
  const dayInsightsOpen = featureOpen(user, 'dayInsights');
  const dayInsightsSetup = useMemo(() => (dayInsightsOpen || user.isAdmin
    ? { preview: !dayInsightsOpen, mark: canSeeView(user, 'mark'), accounts } : null), [dayInsightsOpen, user, accounts]);
  // The account size behind "% of account": the account shown, or all shown accounts added up
  // when every one of them has a size.
  const shownAccounts = accounts.filter((a) => (shownIds ? shownIds.includes(a._id) : !a.archived));
  const displaySize = shownAccounts.length && shownAccounts.every((a) => a.size) ? shownAccounts.reduce((sum, a) => sum + a.size, 0) : 0;

  // Archived accounts live on only in My Trading Journey: the journal's pages (Home, Dashboard,
  // Trades, Insights, the routine, Goals, the Academy) leave their trades out, as if the account
  // were gone. The Accounts page and the Journey still see every trade.
  const journalTrades = useMemo(() => {
    const archived = new Set(accounts.filter((a) => a.archived).map((a) => a._id));
    return archived.size ? trades.filter((x) => !archived.has(x.accountId)) : trades;
  }, [trades, accounts]);
  const visibleTrades = shownIds ? trades.filter((t) => shownIds.includes(t.accountId)) : journalTrades;
  // Why a trade matches a behavior pattern (revenge, giving back the day's profit…), shown in the trade window.
  const behaviorFlags = useMemo(() => (editing ? detectBehavior(trades, { accounts, notes }).flags : new Map()), [editing, trades, accounts, notes]);
  // What the Dashboard and Trades pages show: the accounts picked, then the filters and date range.
  const scopedTrades = applyFilters(visibleTrades, filters, range);
  // What the insights rest on, in words, and the comparisons that make no sense for what's picked
  // (only Mondays means no day-of-week pattern).
  const narrowed = countFilters(filters) > 0 || range.preset !== 'all';
  const scopeLine = [
    t(scopedTrades.length === 1 ? '{n} trade' : '{n} trades', { n: scopedTrades.length }),
    rangeText(range, t),
    ...describeFilters(filters, plays, t),
  ].join(' · ');
  const hideAreas = [
    (filters.timeFrom || filters.timeTo) && 'Time of day',
    filters.weekdays.length && 'Day of week',
    filters.sides.length === 1 && 'Direction',
    filters.symbols.length === 1 && 'Markets',
    filters.grades.length && 'Grade',
  ].filter(Boolean);
  const hiddenNote = hideAreas.length ? t('Patterns about {areas} are hidden, because your filters already pick one of them.', {
    areas: hideAreas.map((a) => t({ 'Time of day': 'time of day', 'Day of week': 'day of the week', Direction: 'long vs. short', Markets: 'symbols', Grade: 'grades' }[a])).join(', '),
  }) : '';
  const widen = () => { setFilters(EMPTY_FILTERS); setRange({ preset: 'all', from: '', to: '' }); };
  const toolbar = (
    <TradeToolbar trades={visibleTrades} library={library} plays={plays} filters={filters} setFilters={setFilters} range={range} setRange={setRange}
      size={displaySize} accountProps={{ accounts, trades, selected: selection, onSelect: selectAccount, onManage: () => navigate('/app/accounts') }} />
  );
  const filterNote = (
    <FilterNote shown={scopedTrades.length} total={visibleTrades.length} filters={filters} range={range} plays={plays}
      onClearFilters={() => setFilters(EMPTY_FILTERS)} onClearRange={() => setRange({ preset: 'all', from: '', to: '' })} />
  );  // "All accounts", "Topstep 50K", or "2 accounts: Topstep 50K, Apex 50K".
  const scopeName = selected === 'all' ? t('All accounts') : selected === 'multi'
    ? t('{n} accounts: {names}', { n: shownIds.length, names: shownIds.map((id) => accountLabel(accountById[id])).join(', ') })
    : accountLabel(accountById[selected]);

  // From "Add account": the file goes into the account just made, previewed on the Trades page.
  async function importIntoAccount(file, account) {
    setImportMessage('');
    setImportFile(file);
    const result = { fileName: file.name, ...parseTradesCsv(await file.text()) };
    const assignment = initialAssignment(result, [...accounts, account], account._id);
    for (const number of Object.keys(assignment.numbers)) assignment.numbers[number] = { accountId: account._id };
    assignment.fallback = { ...assignment.fallback, accountId: account._id };
    setCsvResult(result);
    setAssignment(assignment);
    navigate('/app', { state: { scrollTo: 'import' } });
  }

  async function handleFile(e) {
    const files = [...(e.target.files || [])];
    const file = files[0];
    setImportMessage('');
    setImportFile(file || null);
    if (!file) {
      setCsvResult(null);
      return;
    }
    // Screenshots and PDFs: Claude reads the trades out of them (aiImport.js).
    if (canReadPictures(user) && files.every(isPicture)) {
      setCsvResult(null);
      setReadingPictures(files.length);
      try {
        const result = await readPictures(files);
        setCsvResult(result);
        setAssignment(initialAssignment(result, accounts, selected));
      } catch (err) {
        if (err.status === 401) onSessionExpired();
        else setImportMessage({ ok: false, text: err.message });
      } finally {
        setReadingPictures(0);
      }
      return;
    }
    const text = await file.text();
    const result = { fileName: file.name, ...parseTradesCsv(text) };
    setCsvResult(result);
    setAssignment(initialAssignment(result, accounts, selected));
  }

  // Files without their own commission column can have a per-contract rate applied.
  const canAddCommission = csvResult && csvResult.format && csvResult.format.name === 'orders' && !csvResult.format.hasFees;
  const commissionRate = Number(commission);
  const commissionValid = commission === '' || (!isNaN(commissionRate) && commissionRate >= 0);

  function tradesToImport() {
    if (!canAddCommission || commission === '' || !commissionValid) return csvResult.trades;
    return csvResult.trades.map((t) => ({ ...t, fees: roundTripCommission(t, commissionRate) }));
  }

  function resetImport() {
    setCsvResult(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  async function handleImport() {
    setImporting(true);
    setImportMessage('');
    try {
      const { trades: withAccounts, created } = await resolveImportAccounts(tradesToImport(), assignment);
      if (created.length) setAccounts((list) => [...list, ...created]);
      const { saved, duplicates, feesUpdated } = await api('/trades/bulk', { method: 'POST', body: withAccounts });
      if (!trades.length && saved.length) trackFirstTrade('import');
      if (feesUpdated) {
        // Costs changed on trades already in the journal, so reload them all.
        setTrades(await api('/trades'));
      } else {
        setTrades([...trades, ...saved]);
      }
      refreshAccounts();
      let message = t(saved.length === 1 ? 'Imported {n} trade.' : 'Imported {n} trades.', { n: saved.length });
      if (duplicates > 0) {
        message += ` ${t('Skipped {n} already in your journal.', { n: duplicates })}`;
      }
      if (feesUpdated > 0) {
        message += ` ${t('Added missing commissions to {n} of them.', { n: feesUpdated })}`;
      }
      setImportMessage({ text: message, ok: true });
      resetImport();
    } catch (err) {
      handleApiError(err, (text) => setImportMessage({ text, ok: false }));
    } finally {
      setImporting(false);
    }
  }

  function sortBy(key) {
    const column = TRADE_COLUMNS.find((c) => c.key === key);
    const next = tradeSort.key === key
      ? { key, dir: tradeSort.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: column.numeric ? 'desc' : 'asc' };
    setTradeSort(next);
    writeStored(TRADE_SORT_KEY, JSON.stringify(next));
  }

  const sortValue = {
    date: (t) => new Date(t.date).getTime(),
    account: (t) => accountLabel(accountById[t.accountId]).toLowerCase(),
    symbol: (t) => t.symbol,
    side: (t) => t.side || 'Long',
    size: (t) => t.size || 1,
    entry: (t) => t.entryPrice,
    exit: (t) => t.exitPrice,
    duration: holdMs,
    pnl: grossPnl,
    fees: (t) => t.fees || 0,
    net: netPnl,
    grade: (t) => gradeScore(t.grade),
  }[tradeSort.key] || ((t) => new Date(t.date).getTime());
  // Search (symbol, side, tags, notes, account) and filters on the Trades page, in every view.
  const query = tradeQuery.trim().toLowerCase();
  const filteredTrades = scopedTrades.filter((tr) => {
    if (!query) return true;
    const text = [tr.symbol, tr.side || 'Long', ...(tr.tags || []), tr.notes || '', tr.grade || '', accountLabel(accountById[tr.accountId])].join(' ').toLowerCase();
    return query.split(/\s+/).every((word) => text.includes(word));
  });
  const filtering = Boolean(query);

  // The trades shown (after search and filters) as a spreadsheet file.
  function exportTrades() {
    const cell = (v) => {
      const str = v === null || v === undefined ? '' : String(v);
      const safe = /^[=+\-@\t\r]/.test(str) && !/^-?\d/.test(str) ? `'${str}` : str;
      return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const head = ['date', 'exitDate', 'account', 'symbol', 'side', 'size', 'entryPrice', 'exitPrice', 'grossPnl', 'fees', 'netPnl', 'grade', 'tags', 'notes'];
    const rows = [...filteredTrades].sort((a, b) => new Date(a.date) - new Date(b.date)).map((tr) => [
      new Date(tr.date).toISOString(), tr.exitDate ? new Date(tr.exitDate).toISOString() : '', accountLabel(accountById[tr.accountId]), tr.symbol, tr.side || 'Long',
      tr.size || 1, tr.entryPrice, tr.exitPrice, (grossPnl(tr)).toFixed(2), (tr.fees || 0).toFixed(2), netPnl(tr).toFixed(2), tr.grade || '', (tr.tags || []).join('; '), tr.notes || '',
    ]);
    const csv = [head, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `market-matrix-trades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  const sorted = [...filteredTrades].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    // Trades without a value (e.g. no exit time) always go last.
    if (va === null || vb === null) return va === vb ? 0 : va === null ? 1 : -1;
    const order = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return (tradeSort.dir === 'asc' ? order : -order) || new Date(b.date) - new Date(a.date);
  });

  // Leaving the demo deletes its sample copy; the visitor lands on sign-up or the homepage.
  function leaveDemo(to) {
    navigate(to);
    onLogout();
  }

  // Traders with both prop and personal accounts pick a side (prop firms first); others see it all.
  const journeyScope = hasBothKinds(accounts) ? (JOURNEY_SCOPES.includes(journeyScopePick) ? journeyScopePick : 'prop') : 'all';
  const pickJourneyScope = (next) => {
    setJourneyScopePick(next);
    try {
      localStorage.setItem('mm-journey-scope', next);
    } catch {
      // Private mode: the choice lasts until the page reloads.
    }
  };
  const journey = scopeJourney(journeyScope, accounts, trades);

  // The My Trading Journey pages, also shown blurred behind the Pro offer on the Free plan.
  const journeyPage = (v) => ({
    // The new Journey's three parts.
    jmoney: <JourneyMoney accounts={journey.accounts} trades={journey.trades} onSessionExpired={onSessionExpired}
      onAccountAdded={(created) => setAccounts((list) => [...list, created])}
      onOpenTrades={(id, importNow) => { selectAccount(id); navigate('/app', importNow ? { state: { scrollTo: 'import' } } : undefined); }} />,
    jbetter: <JourneyBetter accounts={journey.accounts} trades={journey.trades} notes={notes} plays={plays} experiments={experiments} user={user} />,
    jgrowth: <JourneyGrowth accounts={journey.accounts} trades={journey.trades} notes={notes} library={library} plays={plays} user={user} onUserChange={onUserChange} />,
    jidentity: <JourneyIdentity accounts={journey.accounts} trades={journey.trades} notes={notes} />,
  }[v] || null);
  // The whole career on the side the switch shows, not just the account picked in the sidebar.
  const journeyEdge = { trades: journey.trades, library, plays };

  const inAcademy = ACADEMY_VIEWS.includes(view);
  return (
    <div className={`app-frame${navCollapsed ? ' nav-collapsed' : ''}${focusMode && view === 'session' ? ' focus-mode' : ''}${inAcademy ? ' academy-frame' : ''}`}>
      {/* Matrix Academy has its own top bar and no journal sidebar (Range.js). */}
      {!inAcademy && (
        <AppSidebar view={view} user={user} accounts={accounts} trades={trades} selected={selection} onSelect={selectAccount}
          onManage={() => navigate('/app/accounts')} collapsed={navCollapsed} onToggleCollapse={toggleNav}
          drawerOpen={drawerOpen} onCloseDrawer={() => setDrawerOpen(false)} />
      )}
      {drawerOpen && !inAcademy && <div className="app-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <div className="app-main">
      {inAcademy ? <AcademyTopBar /> : <AppTopBar view={view} user={user} onOpenDrawer={() => setDrawerOpen(true)} onLogout={onLogout} onExitDemo={() => leaveDemo('/')} />}
      {user.isDemo && (
        <>
          <DemoBanner user={user} onSignup={() => leaveDemo('/signup')} onLogin={() => leaveDemo('/login')} />
          <DemoGate onSignup={() => leaveDemo('/signup')} />
        </>
      )}
      <ProGate />
      <TrialBanner user={user} />
      <main className={`journal-main app-content${view === 'journal' || view === 'notebook' || inAcademy || (JOURNEY_VIEWS.includes(view) && view !== 'mark') ? ' app-content-full' : ''}`}>
      {!inAcademy && <PinnedReminders />}
      {!inAcademy && <LimitBanner user={user} accounts={accounts} trades={journalTrades} />}
      <div key={`${view}-${displayMode}`} className="view-flip">
      {view === 'home' ? (
        <Home user={user} trades={journalTrades} notes={notes} accounts={accounts} plays={plays} library={library} loaded={loaded} experiments={experiments}
          onImport={() => navigate('/app', { state: { scrollTo: 'import' } })}
          onOnboarding={handleOnboarding} onSessionExpired={onSessionExpired}
          onAccountChange={(updated) => setAccounts((list) => list.map((a) => (a._id === updated._id ? { ...a, ...updated } : a)))} />
      ) : view === 'dashboard' ? (
        <DashboardPage trades={journalTrades} visibleTrades={scopedTrades} accountTrades={visibleTrades} toolbar={toolbar} filterNote={filterNote} scopeLine={scopeLine} narrowed={narrowed}
          accounts={accounts} selected={selected} library={library} displaySize={displaySize}
          scope={`${scopeName} · ${t('All time')} · ${t('Net of fees')}`} shownIds={shownIds}
          notes={notes} setNotes={setNotes} onAddTrade={setAddingTradeOn}
          onSelectAccount={selectAccount} onManageAccounts={(id) => (typeof id === 'string' ? setManagingAccounts(id) : navigate('/app/accounts'))} onEditTrade={setEditing}
          onAccountChange={(updated) => setAccounts((list) => list.map((a) => (a._id === updated._id ? { ...a, ...updated } : a)))}
          onTradesUpdated={replaceTrades} onShare={() => setSharing(true)} onSessionExpired={onSessionExpired} user={user} onUserChange={onUserChange} />
      ) : view === 'settings' ? (
        <Settings user={user} accounts={accounts} onSynced={reloadAll} onUserChange={onUserChange} onSessionExpired={onSessionExpired}
          onDeleted={() => { onUserChange(null); navigate('/', { replace: true, state: { accountDeleted: true } }); }} />
      ) : view === 'session' ? (
        <Session notes={notes} setNotes={setNotes} focusMode={focusMode} onToggleFocus={() => setFocusMode((f) => !f)}
          sampleDate={user.isDemo && user.demo ? user.demo.lastDate : null}
          accounts={accounts} trades={journalTrades} selected={shownIds || 'all'} experiments={experiments}
          onNewTrades={mergeTrades} onSessionExpired={onSessionExpired} />
      ) : view === 'compare' ? (
        <Navigate to="/app/insights?tab=compare" replace />
      ) : view === 'plans' ? (
        <Plans user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />
      ) : view === 'insights' && isLocked(user) ? (
        <ProLock title={t('Insights is a Pro feature')} />
      ) : view === 'insights' ? (
        <InsightsPage trades={scopedTrades} accountTrades={visibleTrades} toolbar={toolbar} filterNote={filterNote} scopeLine={scopeLine}
          compare={<AccountComparePage accounts={accounts} trades={journalTrades} library={library} shownIds={shownIds} plays={plays} />}
          plays={plays}
          narrowed={narrowed} hide={hideAreas} hiddenNote={hiddenNote} onWiden={widen} notes={notes} library={library} onLibraryChange={setLibrary} onEditTrade={setEditing} onTradesUpdated={replaceTrades} allTrades={journalTrades} experiments={experiments} setExperiments={setExperiments}
          accounts={accounts} onAccountChange={(updated) => setAccounts((list) => list.map((a) => (a._id === updated._id ? { ...a, ...updated } : a)))}
          accountName={scopeName}
          onSessionExpired={onSessionExpired} />
      ) : view === 'playbook' ? (
        <Playbook trades={visibleTrades} plays={plays} setPlays={setPlays} library={library}
          onLibraryReload={() => api('/tags').then(setLibrary).catch(() => {})}
          accountName={scopeName}
          onEditTrade={setEditing} onTradeUpdated={(t) => setTrades((list) => list.map((x) => (x._id === t._id ? t : x)))}
          onSessionExpired={onSessionExpired} />
      ) : view === 'goals' ? (
        <Goals user={user} accounts={accounts} trades={journalTrades} notes={notes} onSessionExpired={onSessionExpired} />
      ) : view === 'academy' || view === 'academy-progress' ? (
        <Suspense fallback={<p className="empty">{t('Loading…')}</p>}>
          <AcademyHome user={user} trades={journalTrades} accounts={accounts} notes={notes} page={view === 'academy-progress' ? 'progress' : 'home'} />
        </Suspense>
      ) : view === 'course' ? (
        <Suspense fallback={<p className="empty">{t('Loading…')}</p>}>
          <CoursePage trades={journalTrades} accounts={accounts} notes={notes} />
        </Suspense>
      ) : view === 'backtest' ? (
        <Suspense fallback={<p className="empty">{t('Loading…')}</p>}>
          <BacktestPage realTrades={trades} />
        </Suspense>
      ) : view === 'practice' ? (
        <Suspense fallback={<p className="empty">{t('Loading…')}</p>}>
          <PracticePage trades={journalTrades} accounts={accounts} notes={notes} />
        </Suspense>
      ) : view === 'notebook' ? (
        <Suspense fallback={<p className="empty">{t('Loading…')}</p>}>
          <Notebook trades={visibleTrades} routineNotes={notes} setRoutineNotes={setNotes} onEditTrade={setEditing} onSessionExpired={onSessionExpired} />
        </Suspense>
      ) : view === 'market' ? (
        <MarketCalendarPage />
      ) : view === 'account-new' ? (
        <AddAccountWizard
          accounts={accounts}
          onCreated={(created) => { setAccounts((list) => [...list, created]); selectAccount(created._id); }}
          onImportFile={(file, created) => importIntoAccount(file, created)}
          onAddManual={() => { navigate('/app'); setAddingTradeOn(dayKey(new Date())); }}
          onSynced={reloadAll}
          onClose={() => navigate('/app/accounts')}
          onSessionExpired={onSessionExpired}
        />
      ) : OLD_JOURNEY_TO_PART[view] ? (
        <Navigate to={OLD_JOURNEY_TO_PART[view]} replace />
      ) : JOURNEY_VIEWS.includes(view) && !canSeeView(user, view) ? (
        <Navigate to="/app/home" replace />
      ) : view === 'mark' && isLocked(user) ? (
        <ProLock title={t('Coach MARK is part of Pro')} text={t('Your AI trading coach: a debrief after every session, an action plan you check off, and 400 credits a month for questions. Your trades and notes are all still here.')} />
      ) : JOURNEY_VIEWS.includes(view) && isLocked(user) ? (
        // Free members see their own Journey blurred behind the offer, so they know what Pro shows them.
        <div className="pro-preview">
          <div className="pro-preview-body" aria-hidden="true" inert=""><JourneyHub trades={journey.trades} accounts={accounts} allTrades={trades} user={user} scope={journeyScope}>{journeyPage(view)}</JourneyHub></div>
          <div className="pro-preview-lock">
            <ProLock title={t('My Trading Journey is part of Pro')}
              text={t('Whether trading is paying for itself in real money, whether you’re getting better, and your edge and milestones over the long run, from your own trades. Upgrade to see it.')} />
          </div>
        </div>
      ) : view === 'mark' ? (
        <MarkPage user={user} trades={visibleTrades} notes={notes} accounts={shownIds ? accounts.filter((a) => shownIds.includes(a._id)) : accounts}
          allAccounts={accounts} selectedIds={shownIds} onSelectAccounts={selectAccount}
          library={library} plays={plays} scope={scopeName} onSessionExpired={onSessionExpired} />
      ) : journeyPage(view) ? (
        <JourneyHub trades={journey.trades} accounts={accounts} notes={notes} user={user} onUserChange={onUserChange} onChanged={reloadAll} onSessionExpired={onSessionExpired}
          allTrades={trades} scope={journeyScope} onScope={hasBothKinds(accounts) ? pickJourneyScope : null} edge={journeyEdge}>
          {journeyPage(view)}
        </JourneyHub>
      ) : view === 'accounts' ? (
        <AccountsPage user={user} accounts={accounts} trades={trades} onSynced={reloadAll} onSessionExpired={onSessionExpired}
          onAdd={() => navigate('/app/accounts/new')}
          onUpload={(id) => { selectAccount(id); navigate('/app', { state: { scrollTo: 'import' } }); }}
          onManage={(id) => setManagingAccounts(typeof id === 'string' ? id : true)}
          onViewTrades={(id) => { selectAccount(id); navigate('/app'); }} />
      ) : view === 'morning' || view === 'reflection' ? (
        <Routine mode={view} trades={visibleTrades} accounts={accounts} allTrades={journalTrades} notes={notes} setNotes={setNotes} experiments={experiments}
          onSessionExpired={onSessionExpired} />
      ) : (
      <>
      <div className="page-title page-title-row" id="overview">
        <div>
          <h1>{t('Trades')}</h1>
          <p className="subtitle">{t('Every trade, on the record. Log it, import it, learn from it.')}</p>
        </div>
        <button type="button" className="page-action" onClick={() => setAddingTradeOn(dayKey(new Date()))}>+ {t('Add a trade')}</button>
      </div>
      {toolbar}
      {filterNote}

      {DISCORD_URL && !discordDismissed && !user.isDemo && (
        <section className="discord-banner">
          <div>
            <strong>{t('Join the Market Matrix community')}</strong>
            <p>{t('Get help, share ideas for new features, and talk trading with other members on Discord.')}</p>
          </div>
          <div className="discord-banner-actions">
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="cta-button small-cta">
              {t('Join Discord')}
            </a>
            <button type="button" className="link-button" onClick={dismissDiscordCard}>
              {t('Dismiss')}
            </button>
          </div>
        </section>
      )}


      {error && <p className="error">{error}</p>}


      {/* Importing starts from Add a trade → Import a file; this box only shows the file being
          imported, or a welcome for a member with no trades yet. */}
      {!csvResult && !importMessage && !trades.length && loaded.trades && (
        <section className="import import-welcome" id="import">
          <h2>{t('Bring in your trades')}</h2>
          <p className="hint">{t('Import a file from your platform, connect TopstepX so trades arrive by themselves, or add one by hand.')}</p>
          <div className="import-welcome-actions">
            <button type="button" onClick={openImport}>{t('Import a file')}</button>
            {!user.isDemo && <Link className="import-welcome-link" to="/app/accounts/new">{t('Connect TopstepX')}</Link>}
            <button type="button" className="link-button" onClick={() => { setAddingTab('manual'); setAddingTradeOn(dayKey(new Date())); }}>{t('Add one by hand')}</button>
          </div>
        </section>
      )}
      {readingPictures > 0 && (
        <section className="import import-reading" id="import" aria-live="polite">
          <h2>{t('Import trades')} {!featureOpen(user, 'aiImport') && <span className="admin-preview-badge">{t('Admin only')}</span>}</h2>
          <p className="import-reading-line"><span className="import-spinner" aria-hidden="true" />
            {t(readingPictures === 1 ? 'Reading your trades from the file… this takes about 20 to 60 seconds.' : 'Reading your trades from {n} files… this takes about 20 to 60 seconds.', { n: readingPictures })}</p>
        </section>
      )}
      {(csvResult || importMessage) && (
      <section className="import" id="import">
        <h2>{t('Import trades')}</h2>
        {csvResult && (
          <p className="hint">
            {t('Wrong file?')}{' '}
            <button type="button" className="link-button" onClick={() => { resetImport(); openImport(); }}>{t('Choose another file')}</button>
          </p>
        )}

        {csvResult && csvResult.missingColumns.length > 0 && (
          <p className="error">
            {t('{file} is missing these columns: {columns}.', { file: csvResult.fileName, columns: csvResult.missingColumns.join(', ') })}
          </p>
        )}
        {csvResult && importFile && !csvResult.fromPicture && (csvResult.missingColumns.length > 0 || !csvResult.trades.length) && (
          <PlatformRequest key={importFile.name + importFile.size} file={importFile} user={user} from="import" />
        )}

        {csvResult && csvResult.fromPicture && (
          <p className="import-format">
            {t('Read from your screenshot or PDF with AI. Check every trade below before importing: prices, times and sizes.')}
            {csvResult.readNote ? ` ${csvResult.readNote}` : ''}
          </p>
        )}
        {csvResult && csvResult.missingColumns.length === 0 && (
          <div className="import-preview">
            <p>
              {tr(csvResult.trades.length === 1 ? 'Found <0>{n}</0> trade in {file}.' : 'Found <0>{n}</0> trades in {file}.',
                [(x) => <strong>{x}</strong>], { n: csvResult.trades.length, file: csvResult.fileName })}
              {csvResult.errors.length > 0 &&
                ` ${t(csvResult.errors.length === 1 ? '{n} row will be skipped:' : '{n} rows will be skipped:', { n: csvResult.errors.length })}`}
            </p>
            {csvResult.format && csvResult.format.name === 'orders' && (
              <p className="import-format">
                {t('Read as an orders export: matched {n} filled orders into trades', { n: csvResult.format.filledOrders })}
                {csvResult.format.skippedUnfilled > 0 &&
                  t(', ignored {n} canceled or unfilled', { n: csvResult.format.skippedUnfilled })}
                .
                {csvResult.format.skippedOther > 0 &&
                  ` ${t('Skipped {n} rows that aren’t trades (dividends, fees, interest and transfers).', { n: csvResult.format.skippedOther })}`}
                {csvResult.format.unmatched > 0 &&
                  ` ${t('Skipped {n} sales of shares bought before this file starts.', { n: csvResult.format.unmatched })}`}
                {csvResult.format.timeUnknown &&
                  ` ${t('This file has dates but no times, so these trades are left out of time-of-day insights.')}`}
                {csvResult.format.stillOpen > 0 &&
                  ` ${t(csvResult.format.stillOpen === 1
                    ? '{n} position was still open at the end of the file and wasn\'t imported.'
                    : '{n} positions were still open at the end of the file and weren\'t imported.', { n: csvResult.format.stillOpen })}`}
                {csvResult.format.hasFees
                  ? ` ${t('Commissions from the file are included in each trade.')}`
                  : ` ${t("This file doesn't include commissions.")}`}
              </p>
            )}
            {csvResult.format && csvResult.format.name === 'trades' && csvResult.format.hasFees && csvResult.format.hasCommissions && (
              <p className="import-format">
                {t("Fees and commissions from the file are both included in each trade's costs ({total} in total).",
                  { total: usd.format(csvResult.trades.reduce((sum, x) => sum + (x.fees || 0), 0)) })}
              </p>
            )}
            {canAddCommission && (
              <label className="commission-field">
                {t('Commission per contract, per side (optional)')}
                <span className="commission-input">
                  <span>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={commission}
                    onChange={(e) => {
                      setCommission(e.target.value);
                      try {
                        localStorage.setItem(COMMISSION_KEY, e.target.value);
                      } catch {
                        // Not remembered in private mode; that's fine.
                      }
                    }}
                    placeholder="0.00"
                  />
                </span>
                <span className="field-hint">
                  {t("Charged on entry and exit, per contract. Check your broker's fee schedule for your rate.")}
                  {commission !== '' && ` ${t('Remembered for your next import.')}`}
                  {commission !== '' && commissionValid && csvResult.trades.length > 0 &&
                    ` ${t('Total for these {n} trades: {total}.', {
                      n: csvResult.trades.length,
                      total: usd.format(csvResult.trades.reduce((sum, x) => sum + roundTripCommission(x, commissionRate), 0)),
                    })}`}
                </span>
              </label>
            )}
            {csvResult.errors.length > 0 && (
              <ul className="import-errors">
                {csvResult.errors.slice(0, 10).map((err) => (
                  <li key={err.line}>{t('Line {n}:', { n: err.line })} {t(err.message, err.vars)}</li>
                ))}
                {csvResult.errors.length > 10 && <li>{t('…and {n} more', { n: csvResult.errors.length - 10 })}</li>}
              </ul>
            )}
            {assignment && (
              <ImportAccounts csvResult={csvResult} accounts={accounts} assignment={assignment} onChange={setAssignment} />
            )}
            <div className="import-actions">
              <button type="button" onClick={handleImport}
                disabled={importing || csvResult.trades.length === 0 || !commissionValid || needsAccountChoice(csvResult, assignment)}>
                {importing ? t('Importing…') : t(csvResult.trades.length === 1 ? 'Import {n} trade' : 'Import {n} trades', { n: csvResult.trades.length })}
              </button>
              <button type="button" className="secondary" onClick={resetImport} disabled={importing}>
                {t('Cancel')}
              </button>
              {needsAccountChoice(csvResult, assignment) && <span className="field-hint">{t('Choose an account above to import.')}</span>}
            </div>
          </div>
        )}

        {importMessage && <p className={importMessage.ok ? 'success' : 'error'}>{importMessage.text}</p>}
        {importMessage && !csvResult && <button type="button" className="link-button" onClick={() => setImportMessage('')}>{t('Dismiss')}</button>}
      </section>
      )}

      <section className="trades" id="trades">
        <h2>
          <button type="button" className="panel-toggle" aria-expanded={tradesOpen} aria-controls="trades-table" onClick={toggleTrades}>
            <span className={`panel-toggle-chevron${tradesOpen ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
            {t('Your trades')}
            <span className="panel-toggle-count">{t(sorted.length === 1 ? '{n} trade' : '{n} trades', { n: sorted.length })}</span>
          </button>
        </h2>
        {tradesOpen && visibleTrades.length > 0 && (
          <div className="trades-toolbar">
            <div className="inbox-filter trades-view" role="group" aria-label={t('Show trades')}>
              {TRADES_VIEWS.map(([id, label]) => (
                <button key={id} type="button" className={`secondary small${tradesView === id ? ' active' : ''}`} aria-pressed={tradesView === id}
                  onClick={() => chooseTradesView(id)}>{t(label)}</button>
              ))}
            </div>
            <div className="trades-tools">
              <input type="search" className="trades-search" value={tradeQuery} onChange={(e) => setTradeQuery(e.target.value)}
                placeholder={t('Search symbol, tag or note…')} aria-label={t('Search trades')} />
              {filtering && (
                <button type="button" className="link-button" onClick={() => setTradeQuery('')}>
                  {t('Clear')}
                </button>
              )}
              <button type="button" className="secondary small" onClick={exportTrades} disabled={!filteredTrades.length}>{t('Export')}</button>
            </div>
          </div>
        )}
        {tradesOpen && filtering && (
          <p className="trades-filter-note">{t('Showing {n} of {total} trades.', { n: filteredTrades.length, total: scopedTrades.length })}</p>
        )}
        {!tradesOpen ? null : visibleTrades.length === 0 ? (
          <p className="empty">{t('No trades yet. Log your first one above or import a CSV.')}</p>
        ) : tradesView !== 'all' ? (
          <TradeDays trades={filteredTrades} mode={tradesView} notes={notes} library={library} size={displaySize}
            showAccount={selected === 'all' || selected === 'multi'} accountLabel={(id) => accountLabel(accountById[id])} onOpenTrade={setEditing} insights={dayInsightsSetup} />
        ) : (
          <>
          <AllTradesSummary trades={filteredTrades} size={displaySize} />
          <div className="table-wrap trades-scroll all-trades-table" id="trades-table" ref={tradesBox}
            style={tradesBoxHeight ? { maxHeight: `min(${tradesBoxHeight}px, 80vh)` } : undefined}>
            <table>
              <thead>
                <tr>
                  {TRADE_COLUMNS.filter((c) => !c.allOnly || selected === 'all' || selected === 'multi').map((c) => {
                    const active = tradeSort.key === c.key;
                    return (
                      <th key={c.key} aria-sort={active ? (tradeSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" className={`sort-button${active ? ' active' : ''}`} onClick={() => sortBy(c.key)}
                          title={t('Sort by {column}', { column: t(c.label) })}>
                          {t(c.label)}
                          <span className="sort-arrow" aria-hidden="true">{active ? (tradeSort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th>{t('Tags')}</th>
                  <th>{t('Notes')}</th>
                  <th><span className="visually-hidden">{t('Actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((trade) => {
                  const gross = grossPnl(trade);
                  const net = netPnl(trade);
                  return (
                    <tr key={trade._id} className="trade-row" tabIndex={0} title={t('Open this trade')}
                      onClick={(e) => { if (!e.target.closest('button, a')) setEditing(trade); }}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); setEditing(trade); } }}>
                      <td>{trade.timeUnknown
                        ? new Date(trade.date).toLocaleDateString(currentLocale(), { dateStyle: 'short' })
                        : new Date(trade.date).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })}</td>
                      {(selected === 'all' || selected === 'multi') && <td className="account-cell">{accountLabel(accountById[trade.accountId])}</td>}
                      <td className="symbol">{trade.symbol}</td>
                      <td className={(trade.side || 'Long') === 'Short' ? 'loss' : 'gain'}>{t(trade.side || 'Long')}</td>
                      <td>{trade.size || 1}</td>
                      <td>{trade.entryPrice.toFixed(2)}</td>
                      <td>{trade.exitPrice.toFixed(2)}</td>
                      <td className="duration-cell">{holdMs(trade) === null ? '—' : formatDuration(holdMs(trade))}</td>
                      <td className={gross >= 0 ? 'gain' : 'loss'}>{formatMoney(gross)}</td>
                      <td>{trade.fees ? usd.format(trade.fees) : '—'}</td>
                      <td className={net >= 0 ? 'gain' : 'loss'}>{showResult({ net, moves: tradeMoves([trade]), size: displaySize, money: formatMoney, t }).text}</td>
                      <td>{trade.grade ? <GradeBadge grade={trade.grade} /> : <span className="sidebar-muted">—</span>}</td>
                      <td className="tags-cell">
                        {(trade.tags || []).map((name) => (
                          <TagChip key={name} name={name} kind={tagKind(name, library)} />
                        ))}
                      </td>
                      <td className="notes">{trade.notes}</td>
                      <td>
                        <button type="button" className="secondary small" onClick={() => setEditing(trade)}>
                          {t('Open')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>

      </>
      )}
      </div>
      </main>
      <MarketAlertRunner />
      <ReminderAlertRunner />
      <ToastHost />
      <footer className="footer footer-links-row">
        <span>Market Matrix &bull; {t('Trading Journal')}</span>
        <Link to="/terms">{t('Terms')}</Link>
        <Link to="/privacy">{t('Privacy')}</Link>
        <Link to="/contact">{t('Contact')}</Link>
      </footer>
      </div>

      {addingTradeOn && (
        <ManualTrade date={addingTradeOn} accounts={activeAccounts} startOn={addingTab} pictures={canReadPictures(user)} picturesPreview={!featureOpen(user, 'aiImport')}
          defaultAccountId={shownIds ? shownIds[0] : activeAccounts[0] ? activeAccounts[0]._id : ''}
          onClose={() => { setAddingTradeOn(null); setAddingTab(null); }} onSessionExpired={onSessionExpired}
          onImportFile={(e) => {
            // Picked a file instead: read it here and show the import preview on the Trades page.
            setAddingTradeOn(null);
            setAddingTab(null);
            if (view !== 'journal') navigate('/app');
            handleFile(e).then(() => setTimeout(() => document.getElementById('import')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150));
          }}
          onSaved={(saved) => {
            if (!trades.length) trackFirstTrade('manual');
            setTrades((list) => [...list, saved]);
            setAddingTradeOn(null);
            setAddingTab(null);
            refreshAccounts();
          }} />
      )}
      {managingAccounts && (
        <AccountManager
          accounts={accounts}
          trades={trades}
          onChange={setAccounts}
          onReload={reloadAll}
          onDeleted={handleAccountDeleted}
          onTradesCleared={handleTradesCleared}
          focusId={typeof managingAccounts === 'string' ? managingAccounts : null}
          onClose={() => setManagingAccounts(false)}
          onSessionExpired={onSessionExpired}
        />
      )}

      {sharing && (
        <ShareCard trades={visibleTrades} account={shownIds && shownIds.length === 1 ? accountById[shownIds[0]] : null} userName={user.name} user={user}
          onClose={() => setSharing(false)} />
      )}

      {view !== 'session' && (
        <QuickJot onSessionExpired={onSessionExpired} onSaved={({ note, linkedTrades }) => {
          if (note) setNotes((prev) => ({ ...prev, [note.date]: note }));
          applyLinkedNotes(linkedTrades);
        }} />
      )}

      {editing && (
        <TradeEditor
          trade={editing}
          behaviorFlags={behaviorFlags.get(editing._id)}
          plays={plays}
          accounts={accounts}
          library={library}
          onLibraryChange={setLibrary}
          onSaved={handleTradeSaved}
          onUpdated={(updated) => {
            setTrades((list) => list.map((x) => (x._id === updated._id ? updated : x)));
            setEditing(updated);
          }}
          onDeleted={handleTradeDeleted}
          onClose={() => setEditing(null)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </div>
  );
}

export default Journal;
